import React, { FC, useContext } from "react";
import { formatDollarsFromCents } from "helpers/currency";
import { TenantContext } from "v3/context/tenant";
import { LocalizationContext } from "v3/context/localization";
import { PaymentImage } from "@pepdirect/v3/ui/PaymentImage";
import { BorderedTenantLogo } from "@pepdirect/v3/ui/BorderedTenantLogo";
import { AddressDetails } from "../AddressDetails";
import { AllowedPaymentMethod } from "v3/services/graphql/generated";
import { getCreditCardImg, getPaymentMethodImg } from "v3/helpers/payment";
import { PaymentDisplayDetails } from "v3/types/payment";
import { paymentMethods } from "v3/constants/payment";
import {
  StyledBoldText,
  StyledWithAddress,
  StyledPayment,
  StyledText,
} from "./index.styled";

interface PaymentDetailsProps {
  isBillingEqualShipping: boolean;
  isReviewStep?: boolean;
  paymentDetails: PaymentDisplayDetails;
}

export const PaymentDetails: FC<PaymentDetailsProps> = ({
  isBillingEqualShipping,
  isReviewStep = false,
  paymentDetails,
}) => {
  const { localization } = useContext(LocalizationContext);
  const { logoSmall, tenant } = useContext(TenantContext);

  const {
    expiresText = "Expires:",
    billingSameAsShippingText = "Billing address is the same as shipping address.",
    cardholderNameText = "Cardholder Name:",
    invoiceText = "Pay by Invoice",
    invoiceTermsText = "Terms according to contract",
  } = localization?.checkout?.orderDetails || {};
  const { giftCardEndingIn = "Gift card ending in" } =
    localization?.checkout?.giftCard || {};

  const {
    cardExpirationDate,
    billingAddress,
    cardType = "",
    lastFour,
    paymentType,
    payPalEmail,
    applePayCardholderName,
    stripeEmail,
    allocatedCreditAvailableAmount,
  } = paymentDetails;

  return (
    <>
      {paymentType === "GiftCard" && (
        <StyledWithAddress isReviewStep={isReviewStep}>
          <StyledPayment>
            <BorderedTenantLogo
              logoAlt={logoSmall?.alt || ""}
              logoSrc={logoSmall?.src || ""}
              tenantId={tenant?.id}
              small
            />
            <span>
              {giftCardEndingIn} <StyledBoldText>{lastFour}</StyledBoldText>
            </span>
          </StyledPayment>
        </StyledWithAddress>
      )}

      {paymentType === AllowedPaymentMethod.CreditCard && (
        <StyledWithAddress isReviewStep={isReviewStep}>
          <StyledPayment>
            <PaymentImage
              src={getCreditCardImg(cardType)}
              width="36"
              height="24"
            />
            <StyledBoldText>{lastFour}</StyledBoldText>
            <span>
              {expiresText}{" "}
              <StyledBoldText>{cardExpirationDate}</StyledBoldText>
            </span>
          </StyledPayment>
          {billingAddress && !isBillingEqualShipping ? (
            <AddressDetails address={billingAddress} />
          ) : (
            <StyledText>{billingSameAsShippingText}</StyledText>
          )}
        </StyledWithAddress>
      )}

      {paymentType === AllowedPaymentMethod.Blik && (
        <StyledWithAddress isReviewStep={isReviewStep}>
          <StyledPayment>
            <PaymentImage
              src={getPaymentMethodImg(paymentMethods.BLIK)}
              width="36"
              height="24"
            />
            <StyledText>{stripeEmail}</StyledText>
          </StyledPayment>
          {billingAddress && !isBillingEqualShipping ? (
            <AddressDetails address={billingAddress} />
          ) : (
            <StyledText>{billingSameAsShippingText}</StyledText>
          )}
        </StyledWithAddress>
      )}
      {paymentType === AllowedPaymentMethod.P24 && (
        <StyledWithAddress isReviewStep={isReviewStep}>
          <StyledPayment>
            <PaymentImage
              src={getPaymentMethodImg(paymentMethods.P24)}
              width="36"
              height="24"
            />
            <StyledText>{stripeEmail}</StyledText>
          </StyledPayment>
          {billingAddress && !isBillingEqualShipping ? (
            <AddressDetails address={billingAddress} />
          ) : (
            <StyledText>{billingSameAsShippingText}</StyledText>
          )}
        </StyledWithAddress>
      )}

      {paymentType === AllowedPaymentMethod.Paypal && (
        <StyledPayment>
          <PaymentImage
            src={getPaymentMethodImg(paymentMethods.PAYPAL)}
            width="36"
            height="24"
          />
          <StyledText>{payPalEmail}</StyledText>
        </StyledPayment>
      )}

      {paymentType === AllowedPaymentMethod.ApplePay && (
        <StyledPayment>
          <PaymentImage
            src={getPaymentMethodImg(paymentMethods.APPLE_PAY)}
            width="36"
            height="24"
          />
          <StyledBoldText>{lastFour}</StyledBoldText>
          {applePayCardholderName && (
            <span>
              {cardholderNameText}{" "}
              <StyledBoldText>{applePayCardholderName}</StyledBoldText>
            </span>
          )}
        </StyledPayment>
      )}

      {paymentType === AllowedPaymentMethod.Invoice && (
        <StyledPayment>
          <BorderedTenantLogo
            logoAlt={logoSmall?.alt || ""}
            logoSrc={logoSmall?.src || ""}
            tenantId={tenant?.id}
            small
          />
          <StyledBoldText>{invoiceText}</StyledBoldText>
          <span>{invoiceTermsText}</span>
        </StyledPayment>
      )}

      {paymentType === AllowedPaymentMethod.AllocatedCredit && (
        <StyledWithAddress isReviewStep={isReviewStep}>
          <StyledPayment>
            <BorderedTenantLogo
              logoAlt={logoSmall?.alt || ""}
              logoSrc={logoSmall?.src || ""}
              tenantId={tenant?.id}
              small
            />
            <StyledBoldText>Allocation</StyledBoldText>
            <StyledText>
              {allocatedCreditAvailableAmount !== undefined && (
                <>
                  Balance:{" "}
                  {formatDollarsFromCents(allocatedCreditAvailableAmount)}
                </>
              )}
            </StyledText>
          </StyledPayment>
          {billingAddress && !isBillingEqualShipping ? (
            <AddressDetails address={billingAddress} />
          ) : (
            <StyledText>{billingSameAsShippingText}</StyledText>
          )}
        </StyledWithAddress>
      )}
    </>
  );
};
